/* @flow */
/* eslint-env es6,browser */

import { logError } from './utils/logger';
import { isUndefined, isHTMLIFrameElement } from './utils/coreutils';
import {
  createIframe,
  cleanFrame,
  setIfrHeight,
  getIfrDoc
} from './utils/iframeutils';
import { addEvent } from './utils/browserutil';

/**
 * @description Implements adcontainer functionality
 * @exports adcontainer
 * @requires  module:utils
 * @requires module:logger
 */
class _adContainer {
  _iframeHolder: string;

  _sWrapper: string;

  constructor() {
    this._iframeHolder = '';
    this._sWrapper = '';
  }

  /**
   * init
   *
   * @param {module:globalParams.globalParams} g - Global param object
   * @returns {undefined}
   */
  init(g: globalParam) {
    this._iframeHolder = g.iframeHolder;
    this._sWrapper = g.sWrapper;
  }

  /**
   * create Ad Iframe
   *
   * @param {string} adUrl - static template url for loading ad iframe
   * @param {HTMLElement} adDivObj - ad div obj
   * @param {module:adloader.adInfo} adInfoObj - ad info
   * @returns {HTMLElement} -Ad IFrame Element
   * @private
   */
  _createAdIframe(adUrl: string, adDivObj: HTMLElement, adInfoObj: adInfo) {
    const doc = adDivObj.ownerDocument;
    const { as } = adInfoObj;

    // clean Ad frame and container before loading new AD
    cleanFrame(doc, adDivObj, adInfoObj.fID);

    // create iframe and set iframe onload handlers
    const newIF = createIframe(
      doc,
      adInfoObj.fID,
      as.width,
      as.height,
      adUrl,
      isUndefined(as.sandbox) ? adInfoObj.sandbox : as.sandbox
    );

    // attach onload handler for setting iframe height
    if (typeof as.dynHeight !== 'undefined' && as.dynHeight === true) {
      addEvent(newIF, 'load', () => setIfrHeight(adInfoObj.fID));
    }

    // set margin height
    if (typeof as.MarginHeight !== 'undefined') {
      newIF.marginHeight = String(as.MarginHeight);
    }

    // insert new iframe
    adDivObj.appendChild(newIF);
    return newIF;
  }

  /**
   * getter for ad container info
   *
   * @param {module:adloader.adInfo} adInfoObj - Ad Info object
   * @param {boolean} bActive - Active/inactive
   * @returns {undefined}
   */
  /* eslint-disable no-unused-vars */
  _updateAdContainerInfo(adInfoObj: adInfo, bActive: boolean = false): void {
    /* eslint-enable no-unused-vars */
    adInfoObj.fID = adInfoObj.id;
    adInfoObj.cID = adInfoObj.id + this._sWrapper;
  }

  /**
   * getter for div object:
   * handles applications which loads ads inside iframeHolder
   *
   * @param {string} adDivID - div ID
   * @returns {HTMLElement} -Ad IFrame Element
   * @private
   */
  _getAdDiv(adDivID: string): null | HTMLElement {
    let adDivObj = document.getElementById(adDivID);
    if (adDivObj) {
      return adDivObj;
    }
    if (!this._iframeHolder.length) {
      return adDivObj;
    }
    const iframeHolder = document.getElementById(this._iframeHolder);
    if (!isHTMLIFrameElement(iframeHolder)) {
      return adDivObj;
    }

    // $FlowFixMe
    const d = getIfrDoc(iframeHolder);
    if (d) {
      adDivObj = d.getElementById(adDivID);
    }
    return adDivObj;
  }

  /**
   * loader iframe AD
   *
   * @param {string} adUrl - static template url for loading ad iframe
   * @param {module:adloader.adInfo} adInfoObj - ad info
   * @returns {HTMLIFrameElement} - New Ad IFrame
   * @private
   */
  loadIframeAd(adUrl: string, adInfoObj: adInfo): ?HTMLIFrameElement {
    this._updateAdContainerInfo(adInfoObj);
    const adDivObj = this._getAdDiv(adInfoObj.cID);
    if (!adDivObj) {
      logError(`Container not found:${adInfoObj.cID}`);
      // eslint-disable-next-line unicorn/no-useless-undefined
      return undefined;
    }
    return this._createAdIframe(adUrl, adDivObj, adInfoObj);
  }

  /**
   * loading non iframe AD
   *
   * @param {string} adPos - ad position
   * @param {string} data - ad content
   * @returns {undefined}
   * @private
   */
  loadNonIframeAd(adPos: string, data: string) {
    const adDivObj = this._getAdDiv(adPos + this._sWrapper);
    if (adDivObj) {
      adDivObj.innerHTML = data;
    }
  }

  // eslint-disable-next-line no-unused-vars, no-empty-function
  addContainerDivs(adPos: string = '') {}
}

// export singleton object as default for this module
const adcontainer = new _adContainer();
export { adcontainer, _adContainer };
